/**
 * Converte o preenchimento de UM mês no LOTE que o backend grava — e, no
 * caminho de correção, no DELTA que o backend aplica sobre o mês que já existe.
 *
 * É o contrato de "fechar o mês": o usuário volta uma vez por mês, responde os
 * mesmos grupos do questionário para uma única competência, e aquilo é anexado
 * ao histórico em vez de virar um snapshot novo.
 *
 * Espelha `dashboard/backend/services/lote.js`. Três regras vêm de lá e não
 * podem ser afrouxadas aqui:
 *
 * 1. **Categoria é obrigatória** na conta — é ela que reconstrói a árvore do DRE.
 * 2. **Três estados.** Conta ausente = não informado; conta com `valor: 0` =
 *    zero medido; conta com `valor: null` = informada em branco. Por isso
 *    `numeroOuNulo` e NUNCA `Number(x) || 0`: o segundo apaga a diferença entre
 *    "não sei" e "medi e deu zero", e em financeiro zero é uma medição.
 * 3. **Ausência não vira total zero.** Grupo sem nenhuma conta informada não
 *    emite total nenhum (ver `totaisDasContas`).
 */

import { GRUPOS } from '../config/planoContas';

/** Preserva os três estados. É o único ponto de conversão deste arquivo. */
export const numeroOuNulo = (valor) => {
  if (valor === null || valor === undefined) return null;
  const texto = String(valor).trim();
  if (texto === '') return null;
  const n = Number(texto.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Categoria do DRE a partir da categoria do grupo do questionário. */
const CATEGORIA_POR_GRUPO = {
  receita: 'receita.operacional',
  receitaNaoOperacional: 'receita.naoOperacional',
  variavel: 'custo.variavel',
  pessoal: 'custo.fixo.pessoal',
  socios: 'custo.fixo.socios',
  administrativa: 'custo.fixo.administrativas',
  financeira: 'custo.fixo.financeiras',
  marketing: 'custo.fixo.marketing'
};

/** Os buckets de custo fixo que ganham total próprio. */
const CATEGORIA_DO_BUCKET = {
  '2.1.1.1': 'custo.fixo.pessoal',
  '2.1.1.2': 'custo.fixo.socios',
  '2.1.1.3': 'custo.fixo.administrativas',
  '2.1.1.4': 'custo.fixo.financeiras',
  '2.1.1.5': 'custo.fixo.marketing'
};

/**
 * Os totais do mês a partir das contas informadas.
 *
 * Aqui os totais SÃO a soma das contas, e isso é correto: no questionário não
 * existe conta invisível fora da lista — o usuário informou tudo que existe. O
 * que a regra "totais são dado" proíbe é recomputar o total de uma PLANILHA a
 * partir das contas dela, onde a coluna Visível/Invisível faz a soma não fechar.
 * Origem diferente, garantia diferente.
 *
 * AUSÊNCIA NÃO É ZERO. Grupo em que nada foi informado NÃO emite total — a
 * chave não existe, e a tela mostra travessão. O defeito que motivou isto: um
 * mês em que só "Salários" foi preenchido era gravado com faturamento `0` e
 * resultado `-21.000`, duas medições que ninguém fez. Pelo mesmo motivo
 * `custoTotalReal` e `lucroReal` só saem quando TODAS as parcelas deles são
 * conhecidas.
 *
 * Quem realmente não teve nada de um grupo informa `0` numa conta dele — zero é
 * medição e entra. Em branco continua querendo dizer "não informei".
 *
 * Espelha `totaisDasContas` de `backend/services/lote.js`.
 */
export function totaisDasContas(contas) {
  const informadas = (contas || []).filter(
    (c) => c && typeof c.valor === 'number' && Number.isFinite(c.valor)
  );
  const daCategoria = (cat) => informadas.filter((c) => c.categoria === cat);
  const dosFixos = informadas.filter((c) => String(c.categoria || '').startsWith('custo.fixo.'));
  const somar = (lista) => lista.reduce((s, c) => s + c.valor, 0);

  const receitas = daCategoria('receita.operacional');
  const variaveis = daCategoria('custo.variavel');

  const totais = {};
  if (receitas.length) totais['1.1'] = somar(receitas);
  if (variaveis.length) totais['3.2'] = somar(variaveis);
  if (dosFixos.length) totais['2.1.1'] = somar(dosFixos);

  Object.entries(CATEGORIA_DO_BUCKET).forEach(([codigo, categoria]) => {
    const doBucket = daCategoria(categoria);
    if (doBucket.length) totais[codigo] = somar(doBucket);
  });

  const temCustos = totais['3.2'] !== undefined && totais['2.1.1'] !== undefined;
  if (temCustos) totais.custoTotalReal = totais['3.2'] + totais['2.1.1'];
  if (temCustos && totais['1.1'] !== undefined) {
    totais.lucroReal = totais['1.1'] - totais['3.2'] - totais['2.1.1'];
  }

  return totais;
}

/** As contas informadas no formulário, já com categoria e valor numérico. */
export function contasDosGrupos(grupos) {
  const contas = [];
  GRUPOS.forEach((grupo) => {
    const categoria = CATEGORIA_POR_GRUPO[grupo.categoria];
    if (!categoria) return;
    (grupos?.[grupo.id] || []).forEach((conta) => {
      const nome = (conta.nome || '').trim();
      if (!nome) return;
      const valor = numeroOuNulo(conta.valor);
      // Conta deixada em branco NÃO entra: ausência do lote é o que significa
      // "não informado". Incluí-la com null diria "informei que está vazio".
      if (valor === null) return;
      contas.push({ codigo: conta.codigo ?? null, nome, categoria, valor });
    });
  });
  return contas;
}

/**
 * @param {{competencia: string, grupos: Object}} entrada — `grupos` no mesmo
 *   formato do wizard, mas com um campo de valor só por conta (`valor`).
 * @returns {Object} lote pronto para POST /api/periodos
 */
export function loteDoMes({ competencia, grupos }) {
  const contas = contasDosGrupos(grupos);

  return {
    competencia,
    tipo: 'dre',
    origem: 'manual',
    // No questionário a mercadoria é perguntada como custo do mês, então o
    // resultado informado já é de competência — diferente da planilha, cujo
    // "Lucro Real" exclui a compra de estoque e é de caixa.
    regime: 'competencia',
    contas,
    totais: totaisDasContas(contas)
  };
}

/** Um número do lote no formato em que o campo de texto o recebe de volta. */
export const paraCampo = (valor) =>
  typeof valor === 'number' && Number.isFinite(valor) ? String(valor).replace('.', ',') : '';

/**
 * O formulário PRÉ-PREENCHIDO com o mês que já está lançado.
 *
 * Sem isto não existe correção possível: o formulário abria em branco sobre um
 * mês existente e, com "substituir" marcado, trocava sete contas por uma — foi
 * assim que julho perdeu R$ 95.000 de faturamento no exercício de investigação.
 *
 * Conta do lote que não está na lista curada (típico de mês vindo de planilha)
 * é ACRESCENTADA ao grupo da categoria dela, e não escondida: esconder faria a
 * tela mentir sobre o que o mês contém. O que sobra — categoria que o
 * questionário não pergunta, como receita não operacional — volta em
 * `foraDoFormulario`, é preservado pela correção e é dito na tela.
 */
export function gruposDoLote(contas) {
  const doLote = (contas || []).filter((c) => c && c.nome);
  const usadas = new Set();

  const grupos = {};
  GRUPOS.forEach((grupo) => {
    const categoria = CATEGORIA_POR_GRUPO[grupo.categoria];
    const curadas = grupo.contas.map((conta) => {
      const achada = doLote.find((c) => c.categoria === categoria && c.nome === conta.nome);
      if (achada) usadas.add(achada);
      return { ...conta, valor: achada ? paraCampo(achada.valor) : '' };
    });

    const extras = doLote
      .filter((c) => c.categoria === categoria && !usadas.has(c))
      .map((c) => {
        usadas.add(c);
        return { codigo: c.codigo ?? null, nome: c.nome, valor: paraCampo(c.valor) };
      });

    grupos[grupo.id] = [...curadas, ...extras];
  });

  return { grupos, foraDoFormulario: doLote.filter((c) => !usadas.has(c)) };
}

/**
 * O DELTA entre o que estava carregado e o que está na tela agora.
 *
 * A correção manda SÓ o que o usuário tocou. Mandar o mês inteiro é o que
 * transformava "corrigir uma conta" em "apagar todas as outras": o formulário
 * descarta campo vazio, e um campo vazio que nunca foi preenchido é
 * indistinguível de uma conta que o usuário quis apagar.
 *
 * Campo que tinha valor e foi ESVAZIADO vira `valor: null` — "corrigi para em
 * branco", que é diferente de "esta conta não existe". Apagar conta não é
 * oferecido nesta tela.
 */
export function alteracoesDoMes({ grupos, iniciais }) {
  const contas = [];

  GRUPOS.forEach((grupo) => {
    const categoria = CATEGORIA_POR_GRUPO[grupo.categoria];
    if (!categoria) return;

    const antes = new Map(
      (iniciais?.[grupo.id] || []).map((c) => [(c.nome || '').trim(), String(c.valor ?? '').trim()])
    );

    (grupos?.[grupo.id] || []).forEach((conta) => {
      const nome = (conta.nome || '').trim();
      if (!nome) return;
      const agora = String(conta.valor ?? '').trim();
      const original = antes.get(nome);
      if (original !== undefined && original === agora) return;
      // Campo novo que continua vazio não é alteração nenhuma.
      if (original === undefined && agora === '') return;
      contas.push({ codigo: conta.codigo ?? null, nome, categoria, valor: numeroOuNulo(agora) });
    });
  });

  return { contas };
}

/** As contas do mês DEPOIS da correção, para a conferência mostrar o resultado. */
export function contasCorrigidas(vigentes, alteracoes) {
  const contas = [...(vigentes || [])];
  (alteracoes?.contas || []).forEach((nova) => {
    const i = contas.findIndex((c) => c.categoria === nova.categoria && c.nome === nova.nome);
    if (i >= 0) contas[i] = { ...contas[i], ...nova };
    else contas.push({ codigo: nova.codigo ?? null, ...nova });
  });
  return contas;
}

/** Rótulo curto de uma competência, para o texto do preview. */
export const rotuloDaCompetencia = (competencia) => {
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const encontrado = /^(\d{4})-(\d{2})$/.exec(String(competencia || ''));
  if (!encontrado) return String(competencia || '');
  return `${nomes[Number(encontrado[2]) - 1]}/${encontrado[1].slice(2)}`;
};

const NOMES_LONGOS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/** Rótulo longo, o mesmo que a pessoa escolheu no seletor: "Maio de 2026". */
export const rotuloLongoDaCompetencia = (competencia) => {
  const encontrado = /^(\d{4})-(\d{2})$/.exec(String(competencia || ''));
  if (!encontrado) return String(competencia || '');
  return `${NOMES_LONGOS[Number(encontrado[2]) - 1]} de ${encontrado[1]}`;
};

/**
 * "1 conta informada" / "3 contas informadas".
 *
 * Existe porque a tela dizia "1 contas informadas." e "ainda cabem 0 meses" —
 * texto errado num produto financeiro faz o usuário duvidar do número que está
 * do lado.
 */
export const plural = (n, singular, pluralForma) =>
  `${n} ${Number(n) === 1 ? singular : pluralForma}`;
